import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Pill } from '../../components/primitives';
import { api } from '../../lib/api';
import type { MorningBriefRow } from '../../../shared/morningBrief';
import { briefClock, briefDayLabel, briefItemCount, briefModelLabel } from '../../lib/morningBrief';
import { BriefSections } from '../Overview/MorningBriefPanel';
import '../Overview/morningBrief.css';

// Morning brief card foot (mockup M5, approach-B amendments): the OS cannot
// start the routine, so "Regenerate on next run" sets the server flag the
// next facts fetch honours, and "Run now ↗" opens the routine on claude.ai.

export function MorningBriefFoot({ routineUrl, isMobile }: { routineUrl: string; isMobile: boolean }) {
  const [requested, setRequested] = useState(false);
  const [pending, setPending] = useState(false);
  const [briefs, setBriefs] = useState<MorningBriefRow[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api<{ requested: boolean }>('/api/admin/morning-brief/regenerate-request')
      .then((d) => { if (live) setRequested(d.requested === true); })
      .catch(() => {});
    api<{ briefs: MorningBriefRow[] }>('/api/admin/morning-brief/history?limit=100')
      .then((d) => { if (live) setBriefs(Array.isArray(d.briefs) ? d.briefs : []); })
      .catch(() => { if (live) setBriefs([]); });
    return () => { live = false; };
  }, []);

  const setRegenerate = (next: boolean) => {
    if (pending) return;
    setPending(true);
    setError(null);
    api<{ requested: boolean }>('/api/admin/morning-brief/regenerate-request', { method: next ? 'POST' : 'DELETE' })
      .then((d) => setRequested(d.requested === true))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not update the request.'))
      .finally(() => setPending(false));
  };

  const count = briefs?.length ?? 0;
  const hasBrief = (briefs ?? []).some((b) => b.status === 'ok');
  const runNow = routineUrl.trim() || null;

  const regenerate = requested ? (
    <span className="atm-foot-link primary queued">
      Regeneration queued ·{' '}
      <button type="button" className="atm-link-btn" disabled={pending} onClick={() => setRegenerate(false)}>undo</button>
    </span>
  ) : (
    <button type="button" className="atm-foot-link primary atm-link-btn" disabled={pending} onClick={() => setRegenerate(true)}>
      Regenerate on next run
    </button>
  );

  return (
    <>
      {isMobile ? (
        <div className="atm-m-btns">
          {requested ? (
            <button type="button" className="cbtn" disabled={pending} onClick={() => setRegenerate(false)}>
              Regeneration queued · undo
            </button>
          ) : (
            <button type="button" className="cbtn" disabled={pending} onClick={() => setRegenerate(true)}>
              Regenerate on next run
            </button>
          )}
          {runNow && <a className="cbtn" href={runNow} target="_blank" rel="noreferrer">Run now ↗</a>}
          {hasBrief && <Link className="cbtn" to="/overview">Open on Overview →</Link>}
          <button type="button" className="cbtn" onClick={() => setHistoryOpen(true)}>Recent briefs ({count})</button>
        </div>
      ) : (
        <div className="atm-foot">
          {regenerate}
          {runNow && <a className="atm-foot-link primary" href={runNow} target="_blank" rel="noreferrer">Run now ↗</a>}
          {hasBrief && <Link className="atm-foot-link" to="/overview">Open on Overview →</Link>}
          <button type="button" className="atm-foot-link atm-link-btn" onClick={() => setHistoryOpen(true)}>
            Recent briefs ({count})
          </button>
        </div>
      )}
      {error && <p className="error-text atm-knob-error">{error}</p>}
      {historyOpen && <BriefHistoryModal briefs={briefs ?? []} isMobile={isMobile} onClose={() => setHistoryOpen(false)} />}
    </>
  );
}

function BriefHistoryModal({ briefs, isMobile, onClose }: { briefs: MorningBriefRow[]; isMobile: boolean; onClose: () => void }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <Modal title="Recent briefs" context={`${briefs.length} stored`} width={760} onClose={onClose}>
      {briefs.length === 0 ? (
        <p className="empty-note">No briefs yet. The first one appears after the routine posts.</p>
      ) : (
        <div className="brief-history">
          {briefs.map((b) => {
            const open = openId === b.id;
            return (
              <div key={b.id} className={`brief-hist${open ? ' open' : ''}`}>
                <button type="button" className="brief-hist-row" aria-expanded={open}
                  onClick={() => setOpenId(open ? null : b.id)}>
                  <span className="d">{briefDayLabel(b.brief_date)}</span>
                  <Pill tone={b.status === 'ok' ? 'ok' : 'danger'}>{b.status === 'ok' ? 'Received' : 'Failed'}</Pill>
                  <span className="meta">{[briefClock(b.generated_at), briefModelLabel(b.model)].filter(Boolean).join(' · ')}</span>
                  <span className="meta">{b.status === 'ok' ? briefItemCount(b.item_count) : ''}</span>
                  <span className="vote">{b.vote === 'useful' ? 'Useful' : b.vote === 'off' ? 'Off' : ''}</span>
                </button>
                {open && (
                  <div className={`brief brief-readonly${isMobile ? ' is-mobile' : ''}`}>
                    {b.status === 'ok' && b.content ? (
                      <>
                        <p className="brief-headline">{typeof b.content.headline === 'string' ? b.content.headline : ''}</p>
                        <BriefSections row={b} mobile={isMobile} slugPrefix="morning-brief-hist-" />
                      </>
                    ) : (
                      <p className="brief-empty">The routine reported: {b.error ?? 'no detail'}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
